/*
  Дома выбранного судебного участка, привязка новых и основание управления.

  Панель под таблицей судов, а не карточка на каждый участок (решение владельца
  14.09.2026): карточки занимали по пол-экрана, и десяток участков не помещался
  никуда. Открывается выбором строки в таблице.

  Сверху вниз (с 01.10.2026, решение владельца - вариант А из макетов): дома
  участка, дома без участка с кнопкой привязки к этому участку
  (`UnassignedList.jsx`) и ручная форма для того, чего в списке нет
  (`BuildingForm.jsx`).

  🔴 Основание управления (14.09.2026, правовая вычитка). Заявление начинается с
  абзаца о том, чем подтверждается право требовать плату, и без протокола общего
  собрания не формируется вовсе. Поэтому дом без основания помечен прямо в
  таблице: узнать об этом в день подачи - поздно.

  🔴 С 19.10.2026 (мастер привязки, вариант В из макетов) у такого дома в строке
  красная метка «нет протокола» с последствием под ней и кнопка «Внести
  протокол» - она открывает тот же шаг, что второй шаг мастера. Раньше протокол
  вносился через «Изменить», и найти его там мог только тот, кто знал, где
  искать. «Изменить» у дома без протокола остаётся, только когда ему нужен ещё
  и полный адрес: другой двери к полному адресу нет. Общее предупреждение под
  таблицей снято - последствие стоит в самой строке, как в макете.

  🔴 Полный адрес (01.10.2026). У дома два адреса: запись выгрузки, по которой
  находятся квартиры, и полный, который печатается в заявлении. В таблице стоит
  тот, что уйдёт в суд, а запись выгрузки - мелко под ним. Дом, заведённый до
  этого дня без населённого пункта, помечен: заявление по нему напечатает
  неполный адрес, пока полный не внесён через «Изменить».

  Действия (привязать, отвязать, внести протокол) держит `SettingsCourtSections`;
  отказ отвязки с 23.10.2026 показывает сама кнопка (`CourtDetachButton`) - у дома
  со сканом она сначала спрашивает подтверждение. Сам список домов - постраничный, его читает
  `useSectionBuildings` (21.10.2026, пагинация серверная). `version` - цифра
  перечитывания: после любого действия родителя список читается заново на той же
  странице.
*/

import { useState } from 'react'

import { Lock } from './AccessContext.jsx'
import BuildingForm from './BuildingForm.jsx'
import { BasisForm, basisText } from './CourtBuildingBasis.jsx'
import { Loading, Notice, Tag } from './Common.jsx'
import CourtDetachButton from './CourtDetachButton.jsx'
import PageBar from './PageBar.jsx'
import ProtocolDialog from './ProtocolStep.jsx'
import ProtocolScanLink from './ProtocolScanLink.jsx'
import UnassignedList from './UnassignedList.jsx'
import { useSectionBuildings } from './useCourtDirectory.js'

export default function CourtBuildings({
  section,
  sections,
  unassignedCount,
  version,
  onAttach,
  onAttachFromList,
  onDetach,
  onPreview,
  onManagement,
  onProtocol,
  onReadScan,
  onSaveScan,
}) {
  const [editing, setEditing] = useState(null)
  const [protocolFor, setProtocolFor] = useState(null)
  // 21.10.2026: дома участка (их сотни у крупного участка) - страницы режет сервер,
  // под таблицей общая панель.
  const { rows, error, bar } = useSectionBuildings(section.id, version)

  return (
    <section className="card">
      <h2 className="section__title">Дома участка № {section.number}</h2>
      <div className="muted">{section.name}</div>

      <Notice error={error} />
      {rows === null && !error ? <Loading what="Читаем дома участка" /> : null}
      {rows !== null && rows.length > 0 ? (
        <>
          <table className="table table--buildings table--fixed">
            <thead>
              <tr>
                <th>Дом</th>
                <th className="table__num">Помещений</th>
                <th>Основание управления</th>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((building) => (
                <tr key={building.id}>
                  <td>
                    <HouseAddress building={building} />
                  </td>
                  <td className="table__num" data-label="Помещений">
                    {building.premises_count}
                  </td>
                  <td className="table__block" data-label="Основание управления">
                    {building.has_management_basis ? (
                      basisText(building)
                    ) : (
                      <>
                        <Tag kind="burn">нет протокола</Tag>
                        <div className="muted">заявления не собираются</div>
                      </>
                    )}
                    <ProtocolScanLink sectionId={section.id} building={building} />
                  </td>
                  {/*
                    Подписи колонки у этой ячейки нет намеренно: на узком экране
                    строка разворачивается в карточку, и над кнопкой «Отвязать»
                    вставало слово «ДЕЙСТВИЕ» - подпись к тому, что и так названо
                    (осмотр на 390 пикселях, 11.09.2026).
                  */}
                  <td>
                    <Lock>
                      {/*
                        Дому с протоколом, но без скана, предлагается приложить скан
                        (21.10.2026): скан идёт приложением к пакету. Со сканом -
                        заменить его другим.
                      */}
                      {building.has_management_basis && !onReadScan ? null : (
                        <>
                          <button
                            className={building.has_management_basis ? 'button button--quiet' : 'button'}
                            type="button"
                            onClick={() => setProtocolFor(building)}
                          >
                            {protocolButton(building)}
                          </button>{' '}
                        </>
                      )}
                      {building.has_management_basis || building.needs_full_address ? (
                        <>
                          <button
                            className="button button--quiet"
                            type="button"
                            onClick={() => setEditing(building)}
                          >
                            Изменить
                          </button>{' '}
                        </>
                      ) : null}
                      {/* У дома со сканом - сначала окно: скан удаляется вместе с
                          домом безвозвратно (23.10.2026, `CourtDetachButton`). */}
                      <CourtDetachButton
                        building={building}
                        onDetach={() => onDetach(section.id, building.id)}
                      />
                    </Lock>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PageBar {...bar} what="домов" />
        </>
      ) : null}
      {rows !== null && rows.length === 0 ? (
        <p className="muted">
          Домов пока нет. Пока дом не привязан, заявление по его помещениям не формируется.
        </p>
      ) : null}

      {protocolFor ? (
        <ProtocolDialog
          building={protocolFor}
          onSave={(values) => onProtocol(section.id, protocolFor.id, values)}
          onReadScan={onReadScan ? (file) => onReadScan(section.id, protocolFor.id, file) : null}
          onSaveScan={(file, values, replace) =>
            onSaveScan(section.id, protocolFor.id, file, values, replace)
          }
          onClose={() => setProtocolFor(null)}
        />
      ) : null}

      {editing ? (
        <BasisForm
          building={editing}
          onCancel={() => setEditing(null)}
          onSave={async (values) => {
            const done = await onManagement(section.id, editing.id, values)
            if (done) setEditing(null)
          }}
        />
      ) : null}

      <UnassignedList
        section={section}
        sections={sections}
        count={unassignedCount}
        version={version}
        onAttach={onAttachFromList}
      />
      <BuildingForm section={section} onAttach={onAttach} onPreview={onPreview} />
    </section>
  )
}

function protocolButton(building) {
  if (!building.has_management_basis) return 'Внести протокол'
  return building.protocol_scan ? 'Заменить скан' : 'Приложить скан'
}

function HouseAddress({ building }) {
  const shown = building.full_address || building.address
  return (
    <>
      {shown}
      {building.full_address && building.full_address !== building.address ? (
        <div className="muted">в выгрузке: {building.address}</div>
      ) : null}
      {building.needs_full_address ? (
        <div>
          <Tag kind="warn">нужен полный адрес</Tag>
        </div>
      ) : null}
    </>
  )
}
